import React, { useCallback, useEffect, useRef } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import { Locate, Check, MoreHorizontal } from 'lucide-react-native';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { useUIStore } from '../../store/uiStore';
import { planRowButtonId } from '../../hooks/usePlanShortcuts.web';
import { Button } from '../common/Button';
import { Tooltip } from '../common/Tooltip';
import { Popover } from '../common/Popover';
import { DEFENSE_STRATEGIES, copyTargetStrategies, strategyBadgeColors } from './defenseStrategies';
import { formatFeatureMeta } from '../../utils/planFeatureFormat';
import { featureWhoText, featureStatusDisplay } from '../../utils/planAssignmentSummary';
import type {
  DefensePlanFeature,
  DefensePlanAssignment,
  DefenseStrategy,
  PlanParty,
} from '../../types';

// "Who's responsible" for one row: the feature's linked parties (a defense
// can have several) plus the actions available to whoever's looking. A
// caller sets at most one action group: the owner gets onAssign ("Assign"
// with nothing linked, "Edit" once something is); a signed-in-or-gated
// viewer who isn't already on this defense gets onVolunteer; the viewer's
// OWN link (at most one, since a homeowner's party is reused across
// defenses) gets onMarkDone (while accepted) and onStepBack. Leaving `who`
// off entirely renders no second line at all.
export interface PlanFeatureWho {
  links: DefensePlanAssignment[];
  parties: Record<string, PlanParty>;
  /** The plan keyboard layer is live and "A" opens Assign
   *  (usePlanShortcuts). The key acts on the selected row, so only that
   *  row's Assign shows the hint. Omitted in the viewers. */
  assignShortcutLive?: boolean;
  onAssign?: () => void;
  onVolunteer?: () => void;
  onMarkDone?: () => void;
  onStepBack?: () => void;
}

interface RowAction {
  key: string;
  title: string;
  onPress: () => void;
  testID: string;
  /** Key hint for the tooltip, set only while that key works. */
  shortcut?: string;
}

// In practice this is never more than two long: onAssign is owner-only,
// onVolunteer/onMarkDone/onStepBack are viewer-only, and onVolunteer only
// applies when the viewer has no link of their own — so the only two-action
// case is a viewer's own accepted link (Mark done + Step back).
function actionsForWho(who: PlanFeatureWho, featureId: string, selected: boolean): RowAction[] {
  const actions: RowAction[] = [];
  if (who.onAssign) {
    actions.push({
      key: 'assign',
      title: who.links.length > 0 ? 'Edit' : 'Assign',
      onPress: who.onAssign,
      testID: `plan-assign-${featureId}`,
      shortcut: who.assignShortcutLive && selected ? 'A' : undefined,
    });
  }
  if (who.onVolunteer) {
    actions.push({
      key: 'volunteer',
      title: 'I can help',
      onPress: who.onVolunteer,
      testID: `plan-volunteer-${featureId}`,
    });
  }
  if (who.onMarkDone) {
    actions.push({
      key: 'markDone',
      title: 'Mark done',
      onPress: who.onMarkDone,
      testID: `plan-mark-done-${featureId}`,
    });
  }
  if (who.onStepBack) {
    actions.push({
      key: 'stepBack',
      title: 'Step back',
      onPress: who.onStepBack,
      testID: `plan-step-back-${featureId}`,
    });
  }
  return actions;
}

// The responder-capture trick ConfirmDialog's card (and
// DefensePlanBundleRow's own actions row) uses to keep a press here from
// reaching the row's own onSelect underneath: the wrapper claims the
// responder and releases without letting it bubble.
/** Marks a control whose press must not start a row drag. */
const NODRAG = { planNodrag: '' };

function ActionButtons({ actions, style }: { actions: RowAction[]; style: object }) {
  if (actions.length === 0) return null;
  return (
    <View
      style={style}
      // Presses here never start a row drag (usePlanListDrag.web.ts).
      // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
      dataSet={NODRAG}
      onStartShouldSetResponder={() => true}
      onResponderRelease={(e) => e.stopPropagation()}>
      {actions.map((action) => {
        const button = (
          <Button
            title={action.title}
            onPress={action.onPress}
            variant="secondary"
            size="xs"
            testID={action.testID}
          />
        );
        return action.key === 'assign' ? (
          <Tooltip key={action.key} label={action.title} shortcut={action.shortcut}>
            {button}
          </Tooltip>
        ) : (
          <React.Fragment key={action.key}>{button}</React.Fragment>
        );
      })}
    </View>
  );
}

// Who and status, as the tail of the row's one meta line: "Line · 0.6 mi ·
// Jason Johnson · Accepted" or "… · Not assigned yet" (Sept 28 polish: the
// status used to take a third line of its own). Nested Text, so it wraps
// and truncates with the rest of the line. The row's single action renders
// as a sibling to the whole text column, vertically centered at the row's
// right edge; two actions (a viewer's own accepted link) still get their
// own line under the text, outside the text column's button, since a
// button can't hold other buttons.
function StatusInline({ who, fresh = false }: { who: PlanFeatureWho; fresh?: boolean }) {
  const { colors } = useTheme();
  const whoText = featureWhoText(who.links, who.parties);
  const status = featureStatusDisplay(who.links);
  const statusColor =
    status?.tone === 'success'
      ? colors.success
      : status?.tone === 'danger'
        ? colors.danger
        : colors.textSecondary;
  // While `fresh` (just assigned), the text remounts on change and fades in.
  const fade = fresh
    ? { key: `${whoText}|${status?.text ?? ''}`, dataSet: { rediAnim: 'fade-in' } }
    : {};
  if (!whoText) {
    return <Text style={{ color: colors.textSecondary }}>Not assigned yet</Text>;
  }
  return (
    <Text style={{ color: colors.textSecondary }} {...fade}>
      {whoText}
      {status && (
        <>
          {' · '}
          <Text style={{ color: statusColor }}>{status.text}</Text>
        </>
      )}
    </Text>
  );
}

// One feature row: strategy badge, label, kind + measurement — shared by the
// plan editor (DefensePlanPanel.web.tsx) and the read-only plan viewer
// (DefensePlanViewer.web.tsx, DefensePlanViewerCard.web.tsx) so the two can't
// drift on how a feature reads. `onDelete` is the only thing that tells them
// apart: omit it and the row draws with no delete control, which is all the
// viewer needs to go read-only. Both testIDs are keyed off the feature id
// alone (not a caller-supplied prefix) so audits and the existing editor
// tests can find a row the same way regardless of which panel drew it.

export const FEATURE_ROW_DELETE_ICON_SIZE = 15;
const DELETE_ICON_SIZE = FEATURE_ROW_DELETE_ICON_SIZE;
/** Width of the one control at a row's right edge, the ⋯ menu: an icon with
 *  its padding. Copy as and Delete live in that menu and on the keyboard, so
 *  Assign sits right beside it. A bundle header gives its own ⋯ the same
 *  width so its Assign and ⋯ land in the members' two columns. */
export const FEATURE_ROW_ACTION_WIDTH = DELETE_ICON_SIZE + SPACING.xxs * 2;
/** Most bundles the row menu lists before its bundle rows scroll. */
const MENU_MAX_VISIBLE_BUNDLES = 8;
const MENU_ROW_HEIGHT = 36;
/** Space before a nested row's badge: its left bar plus the row padding.
 *  The bundle card subtracts it to put member badges under the name. */
export const FEATURE_ROW_NESTED_LEADING = SPACING.xxs + SPACING.xs;

export function CopyMenuRow({
  strategy,
  isDark,
  testID,
  onPress,
}: {
  strategy: DefenseStrategy;
  isDark: boolean;
  testID: string;
  onPress: () => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const def = DEFENSE_STRATEGIES[strategy];
  const badge = strategyBadgeColors(strategy, isDark);
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="menuitem"
      {...hoverProps}
      style={[
        styles.menuRow,
        CURSOR_POINTER,
        { backgroundColor: hovered ? colors.hoverSurface : 'transparent' },
      ]}>
      <View style={[styles.badge, { backgroundColor: badge.tint }]}>
        <def.icon size={12} color={badge.icon} />
      </View>
      <Text style={[styles.menuLabel, { color: colors.text }]}>{def.label}</Text>
    </Pressable>
  );
}

export interface PlanFeatureMenu {
  /** Every bundle in the plan, in list order. */
  bundles: { id: string; name: string }[];
  /** Loose row: add this defense to a bundle. */
  onAddToBundle: (bundleId: string) => void;
  /** Loose row: open the picker with this defense ticked. */
  onNewBundle: () => void;
  /** Set when the row sits in a selection of 2 or more: the menu then acts
   *  on all of them. `anyBundled` picks "Move" over "Add". */
  selection?: { count: number; anyBundled: boolean; onDelete: () => void };
  /** Member row only: takes the defense out of its bundle. Its presence
   *  is what makes the menu a member menu ("Move to", "Remove from"). */
  onRemoveFromBundle?: () => void;
  /** Loose row, editor with bids: opens this one defense for vendor bids.
   *  Bids live on bundles, so this makes a bundle of one first (Glenn, Nov
   *  4 2026: "sometimes things aren't going to be bundled"). */
  onOpenForBids?: () => void;
}

// The always-visible ⋯ on an editable row (Glenn could not find Copy as or
// Delete behind hover icons, Oct 2026). Open state lives in uiStore
// (planRowMenuFeatureId) so Shift+F10, the ContextMenu key and a right
// click can open it. A press here must never reach the row's onSelect, so
// the wrapper uses the same responder-capture trick as the other icons.
function FeatureMoreButton({
  feature,
  label,
  menu,
  onDelete,
  onCopyAs,
}: {
  feature: DefensePlanFeature;
  label: string;
  menu: PlanFeatureMenu;
  onDelete: () => boolean | void;
  onCopyAs?: (strategy: DefenseStrategy) => void;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const id = feature.id;
  const { isDark } = useTheme();
  const open = useUIStore((s) => s.planRowMenuFeatureId === id);
  const copyOpen = useUIStore((s) => s.planCopyMenuFeatureId === id);
  const anchorRef = useRef<View>(null);
  const close = useCallback(() => {
    const ui = useUIStore.getState();
    if (ui.planRowMenuFeatureId === id) ui.setPlanRowMenuFeature(null);
  }, [id]);
  const closeCopy = useCallback(() => {
    const ui = useUIStore.getState();
    if (ui.planCopyMenuFeatureId === id) ui.setPlanCopyMenuFeature(null);
  }, [id]);
  // The C key can open Copy as while this row's menu is up: one menu at a time.
  useEffect(() => {
    if (copyOpen && open) close();
  }, [copyOpen, open, close]);
  const targets = copyTargetStrategies(feature);
  return (
    <View
      style={styles.copyWrap}
      onStartShouldSetResponder={() => true}
      onResponderRelease={(e) => e.stopPropagation()}>
      <Pressable
        ref={anchorRef}
        onPress={(e) => {
          e?.stopPropagation?.();
          useUIStore.getState().setPlanRowMenuFeature(open ? null : id);
        }}
        testID={`plan-feature-more-${id}`}
        accessibilityRole="button"
        accessibilityLabel={`More actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={open || copyOpen}
        {...hoverProps}
        // A press that starts on this control never starts a row drag.
        // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
        dataSet={NODRAG}
        style={[
          styles.delete,
          CURSOR_POINTER,
          (open || copyOpen) && {
            backgroundColor: colors.pressedSurface,
            borderRadius: BORDER_RADIUS.sm,
          },
        ]}>
        <MoreHorizontal
          size={DELETE_ICON_SIZE}
          color={hovered || open || copyOpen ? colors.text : colors.textSecondary}
        />
      </Pressable>
      <Popover
        visible={open}
        anchorRef={anchorRef}
        onClose={close}
        placement="bottom-end"
        gap={SPACING.xxs}
        testID={`plan-feature-more-${id}-menu`}
        accessibilityLabel={`Actions for ${label}`}>
        <View style={styles.menu}>
          <PlanFeatureMenuItems
            feature={feature}
            menu={menu}
            idPrefix={`plan-feature-more-${id}`}
            onClose={close}
            onDelete={onDelete}
            onOpenCopy={
              onCopyAs ? () => useUIStore.getState().setPlanCopyMenuFeature(id) : undefined
            }
          />
        </View>
      </Popover>
      {onCopyAs && (
        <Popover
          visible={copyOpen}
          anchorRef={anchorRef}
          onClose={closeCopy}
          placement="bottom-end"
          gap={SPACING.xxs}
          testID={`plan-feature-copy-${id}-menu`}
          accessibilityLabel={`Copy ${label} as`}>
          <View style={styles.menu}>
            <Text style={[styles.menuTitle, { color: colors.textTertiary }]}>Copy as</Text>
            {targets.map((strategy) => (
              <CopyMenuRow
                key={strategy}
                strategy={strategy}
                isDark={isDark}
                testID={`plan-feature-copy-${id}-${strategy}`}
                onPress={() => {
                  closeCopy();
                  onCopyAs(strategy);
                }}
              />
            ))}
          </View>
        </Popover>
      )}
    </View>
  );
}

/** Body of a defense's action menu: bundle items, then Delete. One
 *  implementation behind the row's ⋯ menu and the map's right-click menu.
 *  `idPrefix` gives the testIDs (`<prefix>-add-<bundleId>`, `-move-`,
 *  `-new-bundle`, `-remove`, `-copy`, `-delete`). The "Copy as another
 *  defense…" item shows only when `onOpenCopy` is set; the right-click menu
 *  omits it because it lists the copy targets itself. */
export function PlanFeatureMenuItems({
  feature,
  menu,
  idPrefix,
  onClose,
  onDelete,
  onOpenCopy,
}: {
  feature: DefensePlanFeature;
  menu: PlanFeatureMenu;
  idPrefix: string;
  onClose: () => void;
  onDelete: () => boolean | void;
  onOpenCopy?: () => void;
}) {
  const selection = menu.selection;
  const inBundle = !!menu.onRemoveFromBundle;
  const currentBundleName = menu.bundles.find((b) => b.id === feature.bundleId)?.name;
  const targets =
    inBundle && !selection ? menu.bundles.filter((b) => b.id !== feature.bundleId) : menu.bundles;
  // With a selection each row names its own action, so no heading.
  const heading = selection
    ? null
    : inBundle
      ? targets.length > 0
        ? 'Move to'
        : null
      : 'Add to bundle';
  return (
    <>
      {heading && <PlanMenuCaption>{heading}</PlanMenuCaption>}
      <BundleListMenu
        bundles={targets}
        labelFor={(b) =>
          selection
            ? `${selection.anyBundled ? 'Move' : 'Add'} ${selection.count} selected to ${b.name}`
            : b.name
        }
        testIDFor={(b) => `${idPrefix}-${inBundle ? 'move' : 'add'}-${b.id}`}
        scrollTestID={`${idPrefix}-bundles`}
        onPick={(b) => {
          onClose();
          menu.onAddToBundle(b.id);
        }}
      />
      {(selection || !inBundle) && (
        <RowMenuItem
          label={selection ? `New bundle from ${selection.count} selected…` : 'New bundle…'}
          testID={`${idPrefix}-new-bundle`}
          onPress={() => {
            onClose();
            menu.onNewBundle();
          }}
        />
      )}
      {inBundle && (
        <RowMenuItem
          label={
            selection
              ? `Remove ${selection.count} selected from bundles`
              : `Remove from ${currentBundleName ?? 'bundle'}`
          }
          testID={`${idPrefix}-remove`}
          onPress={() => {
            onClose();
            menu.onRemoveFromBundle?.();
          }}
        />
      )}
      {menu.onOpenForBids && !selection && !inBundle && (
        <>
          <PlanMenuDivider />
          <RowMenuItem
            label="Open for bids"
            testID={`${idPrefix}-bids-open`}
            onPress={() => {
              onClose();
              menu.onOpenForBids?.();
            }}
          />
        </>
      )}
      <PlanMenuDivider />
      {onOpenCopy && !selection && (
        <RowMenuItem
          label="Copy as another defense…"
          testID={`${idPrefix}-copy`}
          onPress={() => {
            onClose();
            onOpenCopy();
          }}
        />
      )}
      <RowMenuItem
        label={selection ? `Delete ${selection.count} selected` : 'Delete'}
        danger
        testID={`${idPrefix}-delete`}
        onPress={() => {
          onClose();
          if (selection) selection.onDelete();
          else onDelete();
        }}
      />
    </>
  );
}

/** Small grey heading inside a menu. */
export function PlanMenuCaption({ children }: { children: React.ReactNode }) {
  const { colors } = useTheme();
  return <Text style={[styles.menuTitle, { color: colors.textTertiary }]}>{children}</Text>;
}

/** Hairline between menu groups. */
export function PlanMenuDivider() {
  const { colors } = useTheme();
  return <View style={[styles.menuDivider, { backgroundColor: colors.border }]} />;
}

/** The padded column a menu's items sit in. */
export function PlanMenuSurface({ children }: { children: React.ReactNode }) {
  return <View style={styles.menu}>{children}</View>;
}

/** The bundles of a plan as menu rows, scrolling past a few. Shared by the
 *  row's ⋯ menu and the selection bar's "Add to…". */
export function BundleListMenu({
  bundles,
  labelFor,
  testIDFor,
  scrollTestID,
  onPick,
}: {
  bundles: { id: string; name: string }[];
  labelFor: (bundle: { id: string; name: string }) => string;
  testIDFor: (bundle: { id: string; name: string }) => string;
  scrollTestID: string;
  onPick: (bundle: { id: string; name: string }) => void;
}) {
  const rows = bundles.map((b) => (
    <RowMenuItem key={b.id} label={labelFor(b)} testID={testIDFor(b)} onPress={() => onPick(b)} />
  ));
  if (bundles.length > MENU_MAX_VISIBLE_BUNDLES) {
    return (
      <ScrollView
        style={{ maxHeight: MENU_ROW_HEIGHT * MENU_MAX_VISIBLE_BUNDLES }}
        testID={scrollTestID}>
        {rows}
      </ScrollView>
    );
  }
  return <>{rows}</>;
}

function RowMenuItem({
  label,
  onPress,
  testID,
  danger = false,
}: {
  label: string;
  onPress: () => void;
  testID: string;
  danger?: boolean;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="menuitem"
      {...hoverProps}
      style={[
        styles.menuRow,
        CURSOR_POINTER,
        { backgroundColor: hovered ? colors.hoverSurface : 'transparent' },
      ]}>
      <Text
        style={[styles.menuLabel, { color: danger ? colors.danger : colors.text }]}
        numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

export function DefensePlanFeatureRow({
  feature,
  selected,
  onSelect,
  onDelete,
  onCopyAs,
  who,
  displayLabel,
  nested = false,
  active = false,
  animateIn = false,
  tabbable = true,
  menu,
  dropTarget = false,
}: {
  feature: DefensePlanFeature;
  selected: boolean;
  /** Receives the press event so the panel can read Cmd/Ctrl/Shift. */
  onSelect: (e?: unknown) => void;
  /** Omit for a read-only row (the plan viewer) — no delete control renders. */
  onDelete?: () => boolean | void;
  /** Omit for a read-only row. Picks a strategy to copy this defense as. */
  onCopyAs?: (strategy: DefenseStrategy) => void;
  /** Omit to render no "who's responsible" line at all. */
  who?: PlanFeatureWho;
  /** Overrides the row's title — e.g. "Fire break 2" for the second of
   *  several same-strategy features on a plan (see
   *  utils/planFeatureLabels.computeFeatureLabels). Falls back to the
   *  plain strategy label when omitted, so an existing caller that doesn't
   *  pass it keeps compiling and rendering unchanged. */
  displayLabel?: string;
  /** A member row inside a bundle card (DefensePlanBundleGroup): no border
   *  or radius of its own, so the bundle reads as one card rather than a
   *  card holding cards. The group draws the dividers. */
  nested?: boolean;
  /** This row owns the companion that's open (Assign). Holds a fill that
   *  doesn't depend on the pointer, so the row still reads as the one
   *  being worked on after the mouse moves over to the companion. */
  active?: boolean;
  /** Just drawn: play the list-item-in entrance (motion.ts). */
  animateIn?: boolean;
  /** Roving tab stop: false takes the row's button out of the Tab order
   *  (arrow keys reach it instead, see usePlanShortcuts). Defaults to true,
   *  so the viewer's rows are unchanged. */
  tabbable?: boolean;
  /** Omit for a read-only row (the viewers): no ⋯ menu renders. Needs
   *  onDelete too. A loose row lists "Add to bundle"; a row with
   *  onRemoveFromBundle is a bundle member and lists "Move to" and
   *  "Remove from". */
  menu?: PlanFeatureMenu;
  /** A dragged defense would bundle with this row if dropped now: draws a
   *  focus outline and fill (usePlanListDrag.web.ts). */
  dropTarget?: boolean;
}) {
  const { colors, isDark } = useTheme();
  const { hovered: pointerOver, hoverProps: pointerProps } = useWebHover();
  // Hover is shared with the map: the pointer over this row lights its shape
  // (below), and the pointer over its shape lights this row. A boolean
  // selector per row, so a hover re-renders the two rows that change, not
  // the whole list.
  const mapHovered = useUIStore((s) => s.hoveredPlanFeatureId === feature.id);
  const hovered = pointerOver || mapHovered;
  const justAssigned = useUIStore((s) => s.justAssignedFeature?.id === feature.id);
  // In the multi-selection (Cmd/Ctrl/Shift+click), as opposed to the lone anchor.
  const inMulti = useUIStore((s) => s.planMultiSelection?.includes(feature.id) ?? false);
  const reducedMotionRow = useReducedMotion();
  const featureId = feature.id;
  const hoverProps =
    Platform.OS === 'web'
      ? {
          onPointerEnter: (e: unknown) => {
            (pointerProps as { onPointerEnter?: (e: unknown) => void }).onPointerEnter?.(e);
            useUIStore.getState().setHoveredPlanFeature?.(featureId);
          },
          onPointerLeave: (e: unknown) => {
            (pointerProps as { onPointerLeave?: (e: unknown) => void }).onPointerLeave?.(e);
            useUIStore.getState().clearHoveredPlanFeature?.(featureId);
          },
        }
      : pointerProps;
  const def = DEFENSE_STRATEGIES[feature.strategy];
  const badge = strategyBadgeColors(feature.strategy, isDark);
  const label = displayLabel ?? def.label;
  const actions = who ? actionsForWho(who, feature.id, selected) : [];
  const singleAction = actions.length === 1 ? actions : [];
  const dragData =
    menu && onDelete
      ? {
          planDragId: feature.id,
          planDragBundle: feature.bundleId ?? '',
          ...(nested ? null : { planDrop: `feature:${feature.id}` }),
        }
      : null;
  return (
    // The whole row takes a mouse click, but it isn't the button: the row
    // holds the Assign and Delete buttons, and a <button> inside a <button>
    // is invalid (React warned on every render, Sept 24 2026). The text
    // column below is the keyboard and screen-reader target instead.
    <Pressable
      onPress={onSelect}
      testID={`plan-feature-${feature.id}`}
      focusable={false}
      {...(inMulti ? ({ 'aria-selected': true } as object) : null)}
      // dataSet is a react-native-web prop, not in RN's types. An editable
      // row is also a drag source, and a loose one a drop target
      // (usePlanListDrag.web.ts).
      {...(animateIn || dragData
        ? ({
            dataSet: {
              ...(animateIn ? { rediAnim: 'list-item-in' } : null),
              ...dragData,
            },
          } as object)
        : null)}
      {...hoverProps}
      // Right click anywhere on the row opens its ⋯ menu. RN Web passes
      // onContextMenu through Pressable to the DOM node.
      {...(menu && onDelete
        ? ({
            onContextMenu: (e: { preventDefault?: () => void }) => {
              e.preventDefault?.();
              useUIStore.getState().setPlanRowMenuFeature(feature.id);
            },
          } as object)
        : null)}
      style={[
        styles.row,
        nested ? styles.rowNested : styles.rowCard,
        CURSOR_POINTER,
        {
          // Selected (picked on the map) and active (its Assign is open)
          // share the neutral pressed fill: a clear step past hover with no
          // color, since the map already marks the pick in orange (Sept 28:
          // an orange row fill was too loud; a focus ring looked odd inside
          // a bundle).
          backgroundColor:
            active || selected
              ? colors.pressedSurface
              : hovered
                ? colors.hoverSurface
                : 'transparent',
        },
      ]}>
      {dropTarget && (
        <View
          pointerEvents="none"
          testID={`plan-feature-drop-${feature.id}`}
          style={[
            styles.dropOutline,
            { borderColor: colors.borderFocus, backgroundColor: colors.hoverSurface },
          ]}
        />
      )}
      <View style={styles.text}>
        <Pressable
          onPress={onSelect}
          accessibilityRole="button"
          accessibilityLabel={`${label}, ${formatFeatureMeta(feature)}`}
          aria-pressed={selected}
          {...(dragData
            ? ({
                'aria-description': 'Drag onto a bundle, or open More actions',
              } as object)
            : null)}
          nativeID={planRowButtonId(feature.id)}
          tabIndex={tabbable ? 0 : -1}>
          {/* The badge is the strategy's glyph, so it sits with the name, the
            way the Key and the Layers rows pair a glyph with its label. The
            two lines under it then get the row's full width. */}
          <View style={styles.titleRow}>
            <View style={[styles.badge, { backgroundColor: badge.tint }]}>
              <def.icon size={12} color={badge.icon} />
            </View>
            <Text style={[styles.label, { color: colors.text }]} numberOfLines={1}>
              {label}
            </Text>
          </View>
          {/* "Not assigned yet" is short, so it rides the meta line; a named
              party and its status get their own line, or a long name
              truncated the status off the end ("Jason Johnson · Accep…"). */}
          <Text style={[styles.meta, { color: colors.textTertiary }]} numberOfLines={1}>
            {formatFeatureMeta(feature)}
            {who && who.links.length === 0 && (
              <>
                {' · '}
                <StatusInline who={who} fresh={justAssigned} />
              </>
            )}
          </Text>
          {who && who.links.length > 0 && (
            <Text style={[styles.meta, { color: colors.textTertiary }]} numberOfLines={1}>
              <StatusInline who={who} fresh={justAssigned} />
            </Text>
          )}
        </Pressable>
        {actions.length >= 2 && <ActionButtons actions={actions} style={styles.actionsRow} />}
      </View>
      <ActionButtons actions={singleAction} style={styles.singleAction} />
      {justAssigned && (
        <View
          pointerEvents="none"
          testID={`plan-feature-assigned-${feature.id}`}
          style={styles.assignedCheck}
          // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
          dataSet={reducedMotionRow ? undefined : { rediAnim: 'check-out' }}>
          <Check size={12} color={colors.success} />
        </View>
      )}
      {onDelete ? (
        menu && (
          <FeatureMoreButton
            feature={feature}
            label={label}
            menu={menu}
            onDelete={onDelete}
            onCopyAs={onCopyAs}
          />
        )
      ) : (
        // Read-only row (the viewer): no delete control, so a plain locate
        // glyph tells the row apart from static text and says what tapping
        // it does — zoom the map to this feature (onSelect above).
        <View style={styles.trailingIcon}>
          <Locate size={14} color={colors.textTertiary} />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.xs,
  },
  // A loose row (not in a bundle) is a plain row, not a boxed card (Sept
  // 28, the prototype's list): only a bundle, which groups, gets a card.
  // Hover, active and selected show as its fill, and it pads like the
  // "Who's doing it" rows.
  // 1px less on the right than the left: a bundle card's member Assign
  // sits inside the card's 1px border, and this keeps a loose row's Assign
  // in that same column.
  rowCard: {
    borderRadius: BORDER_RADIUS.md,
    paddingLeft: SPACING.sm,
    paddingRight: SPACING.sm - 1,
    paddingVertical: SPACING.sm,
  },
  // Inside a bundle card: a rounded pill inset from the card on both sides
  // (the card pads the members' column; see DefensePlanBundleGroup), so a
  // hover, active or selected fill doesn't run into the card's right edge.
  // No border of its own (the card draws the box); selected shows as the
  // pressed fill, like a loose row. The left padding keeps the badge at
  // FEATURE_ROW_NESTED_LEADING, and the right padding plus the card's
  // inset puts Assign in the header's column.
  rowNested: {
    borderRadius: BORDER_RADIUS.sm,
    paddingLeft: FEATURE_ROW_NESTED_LEADING,
    paddingRight: SPACING.xxs,
  },
  dropOutline: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  badge: {
    width: 20,
    height: 20,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    ...TYPOGRAPHY.label,
    flex: 1,
    minWidth: 0,
  },
  meta: {
    ...TYPOGRAPHY.caption,
    marginTop: 1,
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: SPACING.xs,
    marginTop: SPACING.xxs,
  },
  // Vertically centered against the row's full height (title + meta +
  // status), sitting to the right of the text column instead of costing
  // the row its own line — this is what keeps a one-action row at roughly
  // its pre-existing height.
  singleAction: {
    alignSelf: 'center',
    flexShrink: 0,
  },
  trailingIcon: {
    alignSelf: 'center',
  },
  assignedCheck: {
    position: 'absolute',
    top: SPACING.xxs,
    right: SPACING.xxs,
  },
  delete: {
    alignSelf: 'center',
    padding: SPACING.xxs,
  },
  copyWrap: {
    alignSelf: 'center',
  },
  menu: {
    padding: SPACING.xs,
    minWidth: 180,
  },
  menuTitle: {
    ...TYPOGRAPHY.caption,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.xxs,
  },
  menuDivider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: SPACING.xxs,
  },
  menuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
  },
  menuLabel: {
    ...TYPOGRAPHY.bodySmall,
  },
});
